import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/constants";
import { ShoppingBag, Tag } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", phone: "", address: "", role: "buyer" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await register(form);
      toast.success("Welcome! Your free weekend trial is set — enjoy Yard-skip this Friday through Sunday.");
      navigate("/browse");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("ys_signup_role", form.role);
    const redirectUrl = window.location.origin + "/browse";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const roles = [
    { key: "buyer", label: "I'm a Shopper", desc: "Find & rate sales", icon: ShoppingBag },
    { key: "seller", label: "I'm a Seller", desc: "Post my yard sale", icon: Tag },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-white px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 p-8 ys-fade-up">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6">
          <img src={LOGO_URL} alt="Yard-skip" className="w-11 h-11 rounded-xl" />
          <span className="font-display font-extrabold text-xl">Yard-<span className="text-ys-sky">skip</span></span>
        </Link>
        <h1 className="font-display text-2xl font-bold text-center">Create your account</h1>
        <div className="grid grid-cols-2 gap-3 mt-6">
          {roles.map((r) => (
            <button key={r.key} type="button" onClick={() => setForm({ ...form, role: r.key })}
              data-testid={`role-${r.key}-btn`}
              className={`rounded-2xl border-2 p-4 text-left transition-all ${
                form.role === r.key ? "border-ys-sky bg-sky-50" : "border-slate-200 hover:border-slate-300"
              }`}>
              <r.icon className={`w-6 h-6 ${form.role === r.key ? "text-ys-sky" : "text-slate-400"}`} />
              <p className="font-semibold text-sm mt-2">{r.label}</p>
              <p className="text-xs text-slate-500">{r.desc}</p>
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="space-y-4 mt-5">
          <div>
            <Label htmlFor="name">Full / real name</Label>
            <Input id="name" required value={form.name} onChange={set("name")} data-testid="register-name-input" className="mt-1.5 rounded-xl h-12" />
          </div>
          <div>
            <Label htmlFor="username">Username</Label>
            <Input id="username" required value={form.username} onChange={set("username")} placeholder="e.g. dave_t" data-testid="register-username-input" className="mt-1.5 rounded-xl h-12" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" className="mt-1.5 rounded-xl h-12" />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" data-testid="register-phone-input" className="mt-1.5 rounded-xl h-12" />
          </div>
          {form.role === "seller" && (
            <div>
              <Label htmlFor="address">Listing address</Label>
              <Input id="address" required value={form.address} onChange={set("address")} placeholder="123 Maple St, City, ZIP" data-testid="register-address-input" className="mt-1.5 rounded-xl h-12" />
            </div>
          )}
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password-input" className="mt-1.5 rounded-xl h-12" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full rounded-full h-12 bg-ys-sky hover:bg-sky-700 text-base">
            {loading ? "Creating..." : "Create account"}
          </Button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <div className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400 font-mono">OR</span><div className="h-px bg-slate-200 flex-1" />
        </div>
        <Button type="button" onClick={startGoogle} variant="outline" data-testid="google-auth-btn" className="w-full rounded-full h-12 gap-2 border-slate-300">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" /> Continue with Google
        </Button>
        <p className="text-sm text-center text-slate-500 mt-6">
          Already have an account? <Link to="/login" className="text-ys-sky font-semibold" data-testid="go-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
